import { DatePipe, TitleCasePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';
import { MatMenuModule } from '@angular/material/menu';
import { CopyToClipboardDirective } from '@examdojo/core/copy-to-clipboard';
import { IconComponent } from '@examdojo/core/icon';
import { getTopicTypeColor, Tag, TOPIC_TYPES, TopicType, VerificationStatus } from '@examdojo/core/ontology';
import { MarkdownModule } from 'ngx-markdown';

interface InheritedTopicType {
  value: TopicType;
  inheritedFrom: Tag | null;
}

const STATUS_LABELS: Record<VerificationStatus, string> = {
  verified: 'Verified',
  in_progress: 'In Progress',
};

@Component({
  selector: 'exam-tag-details',
  standalone: true,
  imports: [CopyToClipboardDirective, IconComponent, MarkdownModule, TitleCasePipe, MatMenuModule, DatePipe],
  templateUrl: './tag-details.component.html',
  styleUrls: ['./tag-details.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TagDetailsComponent {
  readonly data = input.required<Tag>();
  readonly tagsMap = input<Map<string, Tag>>(new Map());

  readonly navigateToNode = output<string>();

  readonly katexOptions = {
    throwOnError: false,
    displayMode: false,
    strict: false,
  };

  readonly topicTypes = TOPIC_TYPES;

  readonly parentName = computed(() => {
    const d = this.data();
    if (!d.parent_id) {
      return '';
    }
    const parent = this.tagsMap().get(d.parent_id);
    return parent?.name ?? d.parent_id;
  });

  readonly effectiveTopicType = computed<InheritedTopicType | null>(() => {
    const d = this.data();
    const map = this.tagsMap();

    if (d.topic_type) {
      return { value: d.topic_type, inheritedFrom: null };
    }

    let currentId = d.parent_id;
    while (currentId) {
      const parent = map.get(currentId);
      if (!parent) {
        break;
      }
      if (parent.topic_type) {
        return { value: parent.topic_type, inheritedFrom: parent };
      }
      currentId = parent.parent_id;
    }

    return null;
  });

  readonly effectiveTopicTypeColor = computed(() => {
    const topicInfo = this.effectiveTopicType();
    if (!topicInfo) {
      return '#f3f4f6';
    }
    return getTopicTypeColor(topicInfo.value);
  });

  readonly prerequisiteNames = computed(() => {
    const d = this.data();
    const map = this.tagsMap();
    if (!d.prerequisite_tag_ids || d.prerequisite_tag_ids.length === 0) {
      return [];
    }
    return d.prerequisite_tag_ids.map((prereqId) => ({
      id: prereqId,
      name: map.get(prereqId)?.name ?? prereqId,
    }));
  });

  readonly verificationReviews = computed(() => {
    const verification = this.data().verification;
    if (!verification || verification.length === 0) {
      return [];
    }
    return verification.map((review) => ({
      ...review,
      statusLabel: STATUS_LABELS[review.status] ?? review.status,
    }));
  });

  readonly getTopicTypeColor = getTopicTypeColor;
}
